import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, Phone } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileOpen(false);
  };

  const handleBookingsClick = () => {
    navigate('/bookings');
    setMobileOpen(false);
  };

  const menuItems = [
    { label: 'Home', section: 'hero', isRoute: false },
    { label: 'Services', section: 'services', isRoute: false },
    { label: 'Our Team', section: 'about', isRoute: false },
    { label: 'Contact', section: 'contact', isRoute: false },
    { label: 'Book Appointment', section: '', isRoute: true },
  ];

  const drawer = (
    <Box sx={{ width: 250 }}>
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.label}
            onClick={() => item.isRoute ? handleBookingsClick() : scrollToSection(item.section)}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
          color: 'text.primary',
          top: 0,
        }}
      >
{isMobile ? (
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => scrollToSection('hero')}
            >
              <img
                src="/logo/hbc-tih-jpeg_orig.jpg"
                alt="Health By Chiropractic"
                style={{
                  height: '88px',
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        ) : (
          <Box sx={{ width: '100%' }}>
            {/* Top row with phone and book appointment */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center', 
                gap: 2, 
                py: 0.5,
                px: 2,
                borderBottom: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  (03) 9743 5510
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                sx={{
                  background: 'linear-gradient(45deg, #8bc34a, #689f38)',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.875rem',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #689f38, #558b2f)',
                  },
                }}
                onClick={handleBookingsClick}
              >
                Book Appointment
              </Button>
            </Box>
            
            {/* Main navigation row */}
            <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              {/* Left side navigation */}
              <Box sx={{ display: 'flex', gap: 3, flex: 1 }}>
                <Button
                  color="inherit"
                  onClick={() => scrollToSection('hero')}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    '&:hover': { color: '#8bc34a' },
                  }}
                >
                  Home
                </Button>
                <Button
                  color="inherit"
                  onClick={() => scrollToSection('services')}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    '&:hover': { color: '#8bc34a' },
                  }}
                >
                  Services
                </Button>
              </Box>

              {/* Centered Logo */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mx: 4,
                }}
                onClick={() => scrollToSection('hero')}
              >
                <img
                  src="/logo/hbc-tih-jpeg_orig.jpg"
                  alt="Health By Chiropractic"
                  style={{
                    height: '101px',
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Box>

              {/* Right side navigation */}
              <Box sx={{ display: 'flex', gap: 3, flex: 1, justifyContent: 'flex-end' }}>
                <Button
                  color="inherit"
                  onClick={() => scrollToSection('about')}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    '&:hover': { color: '#8bc34a' },
                  }}
                >
                  Our Team
                </Button>
                <Button
                  color="inherit"
                  onClick={() => scrollToSection('contact')}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    '&:hover': { color: '#8bc34a' },
                  }}
                >
                  Contact
                </Button>
              </Box>
            </Toolbar>
          </Box>
        )}
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawer}
      </Drawer>

      <Toolbar />
    </>
  );
};

export default Header;